import { ArrowUpRight } from "@phosphor-icons/react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SubpageHero from "@/components/site/SubpageHero";
import LocalVisibility from "@/components/site/LocalVisibility";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import useSeo from "@/hooks/useSeo";
import { selectInterest } from "@/lib/inquiry";
import { FAQS, faqJsonLd } from "@/lib/faqs";

const LV_FAQS = FAQS.filter((f) => f.lv);

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
        { "@type": "ListItem", position: 2, name: "Local Visibility", item: "https://webjesters.com/local-visibility" },
      ],
    },
    {
      "@type": "Service",
      "@id": "https://webjesters.com/local-visibility#service",
      name: "Local Visibility",
      serviceType: "Google Business Profile and social media content management",
      provider: { "@id": "https://webjesters.com/#organization" },
      areaServed: { "@type": "Country", name: "United States" },
      url: "https://webjesters.com/local-visibility",
      description:
        "A standalone monthly service that keeps a local service business's Google Business Profile active and its social presence consistent: $149 one-time onboarding (Google Business Profile setup or cleanup), then $299/month for four content pieces published to Google Business Profile and one included social platform (Facebook Page or Instagram professional account), or $348/month with both platforms. Content is prepared from the client's own photos and short clips, with captions, simple branding, and light edits where needed. Includes basic profile upkeep and a brief monthly activity summary. Available with or without a WebJesters website — no website purchase required.",
      offers: [
        { "@type": "Offer", name: "Local Visibility onboarding (Google Business Profile setup or cleanup)", price: "149", priceCurrency: "USD" },
        {
          "@type": "Offer",
          name: "Local Visibility monthly — Google Business Profile + one social platform",
          price: "299",
          priceCurrency: "USD",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "299",
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
        {
          "@type": "Offer",
          name: "Local Visibility monthly — Google Business Profile + Facebook + Instagram",
          price: "348",
          priceCurrency: "USD",
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "348",
            priceCurrency: "USD",
            unitText: "MONTH",
          },
        },
      ],
    },
    faqJsonLd(LV_FAQS),
  ],
};

const PILLARS = [
  ["Who it's for", "Local service businesses that want an active Google listing and consistent socials without doing it themselves — pressure washing, detailing, pest control, landscaping, contractors, scrap yards, and similar trades."],
  ["How it works", "After onboarding, you send photos or clips from your jobs by text or email. We prepare four pieces a month — captions, simple branding, and light edits where needed — send each one to you for approval, publish it to your Google Business Profile and your included social platform (plus a second platform if you've added it), keep the profile basics current, and email you a short monthly summary."],
  ["With or without a website", "Local Visibility is its own service, billed separately from the website plan and from the one-time Google Business Profile setup. You don't need to buy a website first — it works if your site is hosted elsewhere, and it works if you don't have a website at all. If you are a WebJesters website client, the two simply run side by side."],
];

export default function LocalVisibilityPage() {
  useSeo({
    title: "Local Visibility — Google Business Profile & Social Posts | WebJesters",
    description:
      "Four content pieces a month for your Google Business Profile and Facebook or Instagram, from your job photos. $149 onboarding + $299/mo. No website required.",
    path: "/local-visibility",
    jsonLd: JSON_LD,
  });

  return (
    <main id="main-content" data-testid="local-visibility-page" className="relative">
      <Navbar />
      <SubpageHero
        crumb="Local Visibility"
        eyebrow="Local Visibility · Standalone monthly service"
        title="Google Business Profile and social posts, handled for you."
        intro="Local customers check your Google listing and your socials before they call. Local Visibility keeps both active using the photos and clips you already take on the job. We prepare them for posting, with captions, simple branding, and light edits where needed — four content pieces a month, posted to Google and Facebook or Instagram. No filming, no logging in, no posting schedule to keep up with. It's its own service, so you don't need a WebJesters website to use it."
      >
        <div className="mt-8 flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          <a
            href="#contact"
            onClick={() => selectInterest("local_visibility")}
            data-testid="lv-page-cta"
            className="group inline-flex items-center justify-center gap-3 pl-7 pr-2 py-2 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          >
            Talk to us about Local Visibility
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-brand-600 text-white transition-transform group-hover:translate-x-0.5">
              <ArrowUpRight size={16} weight="bold" />
            </span>
          </a>
          <a
            href="#local-visibility"
            className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full border border-white/15 bg-white/[0.03] text-white font-medium hover:bg-brand-700/25 hover:border-brand-500/50 transition-colors"
          >
            See pricing &amp; scope
          </a>
        </div>
      </SubpageHero>

      <LocalVisibility />

      <section data-tone="light" className="tone-light relative py-14 sm:py-24 bg-white border-y border-zinc-200">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid md:grid-cols-3 gap-8 md:gap-10">
          {PILLARS.map(([h, p], i) => (
            <div key={h}>
              <div className="font-mono text-[12px] tracking-[0.2em] text-brand-700">0{i + 1}</div>
              <h2 className="mt-3 font-display font-bold text-xl sm:text-[22px] tracking-tight text-zinc-900">{h}</h2>
              <p className="mt-3 text-[14.5px] text-zinc-600 leading-relaxed">{p}</p>
            </div>
          ))}
        </div>
      </section>

      <FAQ
        items={LV_FAQS}
        tone="light"
        title="Local Visibility questions."
        intro="Straight answers on what's included each month, what isn't, and how billing works."
      />
      <Contact defaultInterest="local_visibility" />
      <Footer />
    </main>
  );
}
